import React, { useEffect, useState, useRef } from "react";
import { ArrowLeft, ArrowRight, ArrowUpRight } from "lucide-react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";

const SkeletonCard = () => {
  return (
    <div
      className="group bg-white rounded-lg overflow-hidden shadow-lg animate-pulse"
      data-aos="zoom-in"
    >
      <div className="relative h-[27rem] sm:h-[32rem] md:h-[34rem] lg:h-[35rem] overflow-hidden bg-gray-300">
        <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/20 to-transparent"></div>
        <div className="absolute inset-0 p-6 md:p-4 lg:p-6 flex flex-col justify-center gap-4">
          <div className="h-8 sm:h-10 md:h-12 lg:h-14 bg-gray-400 rounded w-3/4 mx-auto"></div>
          <div className="h-6 sm:h-8 md:h-10 bg-gray-400 rounded w-5/6 mx-auto"></div>
        </div>
        <div className="absolute bottom-6 left-6 right-6 flex justify-between items-center">
          <div className="flex flex-col gap-1">
            <div className="h-4 w-16 bg-gray-400 rounded"></div>
            <div className="h-3 w-12 bg-gray-400 rounded"></div>
          </div>
          <div className="w-10 h-10 md:w-12 md:h-12 bg-gray-400 rounded-full"></div>
        </div>
      </div>
    </div>
  );
};

const formatTimestamp = (createdAt) => {
  const dateObj = new Date(createdAt);
  const formattedDate = dateObj.toLocaleDateString("en-US", { day: "2-digit", month: "short" });
  const formattedTime = dateObj.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true }).toLowerCase();
  return { date: formattedDate, time: formattedTime };
};

const BlogCard = ({ activity, onMouseEnter, onMouseLeave }) => {
  return (
    <div
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className="group rounded-lg shadow-md hover:shadow-xl transition-all duration-200"
    >
      <div className="relative h-[27rem] sm:h-[32rem] md:h-[34rem] lg:h-[35rem] overflow-hidden rounded-lg flex">
        <img
          src={activity.images[0]}
          alt={activity.title}
          className="w-full h-full object-cover rounded-lg transition-transform duration-700 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/40 to-black/40 transition-opacity duration-300" />
        <div className="absolute inset-0 p-6 md:p-4 lg:p-6 flex flex-col justify-end text-white">
          <div className="flex mt-10 sm:mt-12 md:mt-14 lg:mt-16 items-center justify-between font-extralight">
            {activity?.status === "published" ? (
              <div className="text-sm md:text-base">
                <div>{formatTimestamp(activity.createdAt)?.date}</div>
                <div className="text-gray-300">{formatTimestamp(activity.createdAt)?.time}</div>
              </div>
            ) : (
              <div className="text-sm md:text-base">Coming Soon</div>
            )}
            <div className="w-10 h-10 md:w-12 md:h-12 bg-red-600 hover:bg-red-700 rounded-full flex items-center justify-center transition-all duration-300 transform hover:scale-105 shadow-lg cursor-pointer group-hover:shadow-xl">
              <ArrowUpRight className="w-5 h-5 md:w-6 md:h-6 text-white transition-transform duration-300 group-hover:rotate-12" />
            </div>
          </div>
        </div>
        <div className="absolute inset-0 p-6 md:p-4 lg:p-6 mt-6 w-full h-full flex flex-col gap-y-4 content-center justify-center">
          <h3 className="min-h-[5.2rem] line-clamp-3 text-xl sm:text-2xl md:text-3xl lg:text-4xl text-white font-semibold leading-relaxed tracking-wide">
            {activity.title}
          </h3>
          <p className="min-h-[3rem] line-clamp-2 font-extralight text-sm sm:text-base md:text-lg lg:text-xl text-white leading-loose opacity-90">
            {activity.content}
          </p>
        </div>
      </div>
    </div>
  );
};

const AdditionalInformation = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [slidesToShow, setSlidesToShow] = useState(3);
  const navigate = useNavigate();

  const intervalRef = useRef(null);
  const progressRef = useRef(null);

  useEffect(() => {
    AOS.init({ duration: 800, once: true, easing: "ease-out" });
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const resp = await axios.get(`${process.env.REACT_APP_API_URL}/news-blogs/all-news-blogs`);
      setBlogs(resp.data?.newsBlogs || []);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const handleResize = () => setSlidesToShow(window.innerWidth < 768 ? 1 : 3);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const sortedBlogs = blogs.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)) || [];
  const cardWidth = 100 / slidesToShow;
  const maxSlides = Math.max(0, sortedBlogs.length - slidesToShow);
  const hasMultipleSlides = sortedBlogs.length > slidesToShow;

  const nextSlide = () => setCurrentIndex((prev) => (prev >= maxSlides ? 0 : prev + 1));
  const prevSlide = () => setCurrentIndex((prev) => (prev <= 0 ? maxSlides : prev - 1));
  const handleCardClick = (blog) => { navigate(`/contact`); window.scrollTo({ top: 0, behavior: "smooth" }); };

  // Auto-slide
  useEffect(() => {
    if (!hasMultipleSlides) return;
    intervalRef.current = setInterval(nextSlide, 4000);
    return () => clearInterval(intervalRef.current);
  }, [currentIndex, slidesToShow, hasMultipleSlides]);

  if (loading) {
    return (
      <div className="w-full px-4 md:px-8 lg:px-12 py-12">
        <h2 className="font-heading text-xl md:text-3xl font-bold text-center mb-10 tracking-wide">ADDITIONAL INFORMATION</h2>
        <div className="flex overflow-hidden">
          {Array.from({ length: slidesToShow }).map((_, i) => (
            <div key={i} className="flex-shrink-0 px-2" style={{ width: `${cardWidth}%` }}>
              <SkeletonCard />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full py-12">
      <h2 className="font-heading text-xl md:text-3xl px-4 md:px-8 lg:px-12 font-bold text-center mb-10 tracking-wide">ADDITIONAL INFORMATION</h2>
      <div className="relative w-full px-2 md:px-6 lg:px-10">
        <div className="overflow-hidden">
          <div className="flex transition-transform duration-500 ease-in-out" style={{ transform: `translateX(-${currentIndex * cardWidth}%)` }}>
            {sortedBlogs.map((blog, index) => (
              <div
                key={blog._id}
                className="flex-shrink-0 cursor-pointer px-2"
                style={{ width: `${cardWidth}%` }}
                onClick={() => handleCardClick(blog)}
                data-aos={index % 2 === 0 ? "fade-right" : "fade-left"}
                data-aos-delay={index * 100}
              >
                <BlogCard activity={blog} />
              </div>
            ))}
          </div>
        </div>

        {hasMultipleSlides && (
          <div className="mt-6 relative px-4 md:px-8 lg:px-12 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="h-full w-20" />
            <div className="flex space-x-2">
              {Array.from({ length: maxSlides + 1 }, (_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`w-3 h-1.5 rounded-full transition-all duration-300 ${currentIndex === index ? "bg-red-600" : "bg-gray-300"}`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
            <div className="flex gap-x-2">
              <button onClick={prevSlide} className="bg-primaryBtnHoverColor hover:scale-[101%] active:scale-95 text-white p-2 rounded-full transition-all duration-300 z-10" aria-label="Previous slide">
                <ArrowLeft className="w-6 h-6" />
              </button>
              <button onClick={nextSlide} className="bg-primaryBtnHoverColor hover:scale-[101%] active:scale-95 text-white p-2 rounded-full transition-all duration-300 z-10" aria-label="Next slide">
                <ArrowRight className="w-6 h-6" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdditionalInformation;










// import React, { useEffect, useState, useRef } from "react";
// import { ArrowLeft, ArrowRight, ArrowUpRight } from "lucide-react";
// import axios from "axios";
// import { useNavigate } from "react-router-dom";

// const SkeletonCard = () => {
//   return (
//     <div className="group bg-white rounded-lg overflow-hidden shadow-lg animate-pulse">
//       {/* Image Container */}
//       <div className="relative h-[27rem] sm:h-[32rem] md:h-[34rem] lg:h-[35rem] overflow-hidden bg-gray-300">
//         {/* Gradient overlay like original */}
//         <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/20 to-transparent"></div>

//         {/* Text Overlay (centered) */}
//         <div className="absolute inset-0 p-6 md:p-4 lg:p-6 flex flex-col justify-center gap-4">
//           <div className="h-8 sm:h-10 md:h-12 lg:h-14 bg-gray-400 rounded w-3/4 mx-auto"></div>{" "}
//           {/* Title */}
//           <div className="h-6 sm:h-8 md:h-10 bg-gray-400 rounded w-5/6 mx-auto"></div>{" "}
//           {/* Description */}
//         </div>

//         {/* Bottom Section (date/time + arrow) */}
//         <div className="absolute bottom-6 left-6 right-6 flex justify-between items-center">
//           {/* Date/Time */}
//           <div className="flex flex-col gap-1">
//             <div className="h-4 w-16 bg-gray-400 rounded"></div>
//             <div className="h-3 w-12 bg-gray-400 rounded"></div>
//           </div>

//           {/* Arrow button */}
//           <div className="w-10 h-10 md:w-12 md:h-12 bg-gray-400 rounded-full"></div>
//         </div>
//       </div>
//     </div>
//   );
// };

// const formatTimestamp = (createdAt) => {
//   const dateObj = new Date(createdAt);

//   // Format Date like "14 Apr"
//   const optionsDate = { day: "2-digit", month: "short" };
//   const formattedDate = dateObj.toLocaleDateString("en-US", optionsDate);

//   // Format Time like "12.24 pm"
//   const optionsTime = { hour: "numeric", minute: "2-digit", hour12: true };
//   const formattedTime = dateObj
//     .toLocaleTimeString("en-US", optionsTime)
//     .toLowerCase();

//   return {
//     date: formattedDate,
//     time: formattedTime,
//   };
// };

// const BlogCard = ({ activity, onMouseEnter, onMouseLeave }) => {
//   return (
//     <>
//       <div
//         onMouseEnter={onMouseEnter}
//         onMouseLeave={onMouseLeave}
//         key={activity._id}
//         className="group rounded-lg shadow-md hover:shadow-xl transition-all duration-200"
//       >
//         {/* Image Container */}
//         <div className="relative h-[27rem] sm:h-[32rem]  md:h-[34rem] lg:h-[35rem] overflow-hidden rounded-lg flex ">
//           <img
//             src={activity.images[0]}
//             alt={activity.title}
//             className="w-full h-full object-cover rounded-lg transition-transform duration-700 group-hover:scale-110"
//           />
//           {/* Gradient Overlay */}
//           <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/40 to-black/40  transition-opacity duration-300" />

//           {/* Content Overlay */}
//           <div className="absolute inset-0 p-6 md:p-4 lg:p-6 flex flex-col justify-end text-white">
//             {/* Bottom Section */}
//             <div className="flex mt-10 sm:mt-12 md:mt-14 lg:mt-16 items-center justify-between font-extralight">
//               {/* Date and Time */}
//               {activity?.status === "published" ? (
//                 <div className="text-sm md:text-base">
//                   <div className="">
//                     {formatTimestamp(activity.createdAt)?.date}
//                   </div>
//                   <div className="text-gray-300">
//                     {formatTimestamp(activity.createdAt)?.time}
//                   </div>
//                 </div>
//               ) : (
//                 <div className="text-sm md:text-base">Coming Soon</div>
//               )}

//               {/* Arrow Button */}
//               <div className="w-10 h-10 md:w-12 md:h-12 bg-red-600 hover:bg-red-700 rounded-full flex items-center justify-center transition-all duration-300 transform hover:scale-105 shadow-lg cursor-pointer group-hover:shadow-xl">
//                 <ArrowUpRight className="w-5 h-5 md:w-6 md:h-6 text-white transition-transform duration-300 group-hover:rotate-12" />
//               </div>
//             </div>
//           </div>

//           <div className="absolute inset-0 p-6 md:p-4 lg:p-6 mt-6 w-full h-full flex flex-col gap-y-4 content-center justify-center">
//             <h3 className="min-h-[5.2rem] line-clamp-3  text-xl sm:text-2xl md:text-3xl lg:text-4xl text-white font-semibold  leading-relaxed tracking-wide">
//               {activity.title}
//             </h3>
//             <p className="min-h-[3rem] line-clamp-2 font-extralight text-sm sm:text-base  md:text-lg lg:text-xl  text-white  leading-loose align-middle opacity-90">
//               {activity.content}
//             </p>
//           </div>
//         </div>
//       </div>
//     </>
//   );
// };

// const AdditionalInformation = () => {
//   const [blogs, setBlogs] = useState([]);
//   const [loading, setLoading] = useState(true);

//   // Slider state
//   const [currentIndex, setCurrentIndex] = useState(0);
//   const [isPlaying, setIsPlaying] = useState(true);
//   const [progress, setProgress] = useState(0);
//   const [isUserInteracting, setIsUserInteracting] = useState(false);
//   const [slidesToShow, setSlidesToShow] = useState(3);
//   const navigate = useNavigate();

//   const handleCardClick = (blog) => {
//     // const blogId = blog?._id;
//     // navigate(`/news-blog-details`, { state: { blogId } });
//     navigate(`/contact`);
//     window.scrollTo({
//       top: 0,
//       behavior: "smooth",
//     });
//   };
//   // Refs
//   const intervalRef = useRef(null);
//   const progressIntervalRef = useRef(null);

//   // Slider configuration
//   const SLIDE_DURATION = 4000; // 4 seconds
//   const PROGRESS_UPDATE_INTERVAL = 30; // Update progress every 30ms

//   const fetchData = async () => {
//     try {
//       setLoading(true);
//       const resp = await axios.get(
//         `${process.env.REACT_APP_API_URL}/news-blogs/all-news-blogs`
//       );
//       setBlogs(resp.data?.newsBlogs);
//     } catch (error) {
//       console.error("Error fetching Hero Content", error);
//     } finally {
//       setLoading(false);
//     }
//   };

//   // Get all sorted blogs
//   const sortedBlogs =
//     blogs?.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)) || [];

//   // Calculate max slides (how many times we can slide)
//   const maxSlides = Math.max(0, sortedBlogs.length - slidesToShow);
//   const hasMultipleSlides = sortedBlogs.length > slidesToShow;

//   // Clear all timers
//   const clearTimers = () => {
//     if (intervalRef.current) {
//       clearInterval(intervalRef.current);
//       intervalRef.current = null;
//     }
//     if (progressIntervalRef.current) {
//       clearInterval(progressIntervalRef.current);
//       progressIntervalRef.current = null;
//     }
//   };

//   // Auto-slide logic
//   const nextSlide = () => {
//     if (hasMultipleSlides) {
//       setCurrentIndex((prevIndex) =>
//         prevIndex >= maxSlides ? 0 : prevIndex + 1
//       );
//       setProgress(0);
//     }
//   };

//   const prevSlide = () => {
//     if (hasMultipleSlides) {
//       setCurrentIndex((prevIndex) =>
//         prevIndex <= 0 ? maxSlides : prevIndex - 1
//       );
//       setProgress(0);
//     }
//   };

//   const goToSlide = (index) => {
//     setCurrentIndex(index);
//     setProgress(0);
//   };

//   // Mouse interaction handlers
//   const handleMouseEnter = () => {
//     setIsUserInteracting(true);
//     clearTimers();
//   };

//   const handleMouseLeave = () => {
//     setIsUserInteracting(false);
//     setProgress(progress);
//   };

//   // Manual navigation handlers (also pause the slider)
//   const handlePrevSlide = () => {
//     setIsUserInteracting(true);
//     clearTimers();
//     prevSlide();
//     // Resume after a short delay
//     setTimeout(() => {
//       setIsUserInteracting(false);
//       setProgress(0);
//     }, 1000);
//   };

//   const handleNextSlide = () => {
//     setIsUserInteracting(true);
//     clearTimers();
//     nextSlide();
//     // Resume after a short delay
//     setTimeout(() => {
//       setIsUserInteracting(false);
//       setProgress(0);
//     }, 1000);
//   };

//   const handleGoToSlide = (index) => {
//     setIsUserInteracting(true);
//     clearTimers();
//     goToSlide(index);
//     // Resume after a short delay
//     setTimeout(() => {
//       setIsUserInteracting(false);
//       setProgress(0);
//     }, 1000);
//   };

//   // Handle responsive slides
//   useEffect(() => {
//     const handleResize = () => {
//       if (window.innerWidth < 768) {
//         setSlidesToShow(1); // 1 card on mobile
//       } else {
//         setSlidesToShow(3); // 3 cards on tablet and desktop
//       }
//     };

//     // Set initial value
//     handleResize();

//     // Add event listener
//     window.addEventListener("resize", handleResize);

//     // Cleanup
//     return () => window.removeEventListener("resize", handleResize);
//   }, []);

//   // Progress bar logic
//   useEffect(() => {
//     if (!isPlaying || loading || !hasMultipleSlides || isUserInteracting) {
//       return;
//     }

//     progressIntervalRef.current = setInterval(() => {
//       setProgress((prevProgress) => {
//         const newProgress =
//           prevProgress + (PROGRESS_UPDATE_INTERVAL / SLIDE_DURATION) * 100;
//         return newProgress >= 100 ? 100 : newProgress;
//       });
//     }, PROGRESS_UPDATE_INTERVAL);

//     return () => {
//       if (progressIntervalRef.current) {
//         clearInterval(progressIntervalRef.current);
//       }
//     };
//   }, [isPlaying, loading, hasMultipleSlides, currentIndex, isUserInteracting]);

//   // Auto-slide logic
//   useEffect(() => {
//     if (!isPlaying || loading || !hasMultipleSlides || isUserInteracting) {
//       return;
//     }

//     intervalRef.current = setInterval(() => {
//       nextSlide();
//     }, SLIDE_DURATION);

//     return () => {
//       if (intervalRef.current) {
//         clearInterval(intervalRef.current);
//       }
//     };
//   }, [
//     isPlaying,
//     loading,
//     hasMultipleSlides,
//     currentIndex,
//     isUserInteracting,
//     slidesToShow,
//   ]);

//   // Reset current index when slidesToShow changes
//   useEffect(() => {
//     setCurrentIndex(0);
//     setProgress(0);
//   }, [slidesToShow]);

//   // Cleanup on unmount
//   useEffect(() => {
//     return () => {
//       clearTimers();
//     };
//   }, []);

//   useEffect(() => {
//     fetchData();
//   }, []);
//   const cardWidth = 100 / slidesToShow;

//   if (loading) {
//     const skeletonCardsCount = slidesToShow;

//     return (
//       <div className="w-full px-4 md:px-8 lg:px-12 py-12">
//         <h2 className="font-heading text-xl md:text-3xl font-bold text-center mb-10 tracking-wide">
//           ADDITIONAL INFORMATION
//         </h2>

//         {/* Skeleton loader that matches the actual slider layout */}
//         <div className="relative w-full">
//           <div className="overflow-hidden">
//             <div className="flex">
//               {Array.from({ length: skeletonCardsCount }).map((_, i) => (
//                 <div
//                   key={i}
//                   className="flex-shrink-0 px-2"
//                   style={{ width: `${cardWidth}%` }}
//                 >
//                   <SkeletonCard />
//                 </div>
//               ))}
//             </div>
//           </div>

//           {/* Skeleton controls */}
//           <div className="mt-6 relative flex flex-col sm:flex-row items-center justify-between gap-4">
//             <div className="h-full w-20"></div>

//             {/* Skeleton dots */}
//             <div className="flex space-x-2">
//               {Array.from({ length: 3 }).map((_, index) => (
//                 <div
//                   key={index}
//                   className="w-12 sm:w-20 h-1.5 bg-gray-300 rounded-full animate-pulse"
//                 />
//               ))}
//             </div>

//             {/* Skeleton arrows */}
//             <div className="relative md:right-2 flex gap-x-2">
//               <div className="w-10 h-10 bg-gray-300 rounded-full animate-pulse"></div>
//               <div className="w-10 h-10 bg-gray-300 rounded-full animate-pulse"></div>
//             </div>
//           </div>
//         </div>
//       </div>
//     );
//   }

//   return (
//     <div className="w-full  py-12">
//       {/* Heading */}
//       <h2 className="font-heading text-xl md:text-3xl px-4 md:px-8 lg:px-12 font-bold text-center mb-10 tracking-wide">
//         ADDITIONAL INFORMATION
//       </h2>

//       {/* Slider Container */}
//       <div className="relative w-full px-2 md:px-6 lg:px-10">
//         <div className="overflow-hidden">
//           <div
//             className="flex transition-transform duration-500 ease-in-out"
//             style={{ transform: `translateX(-${currentIndex * cardWidth}%)` }}
//           >
//             {sortedBlogs.map((blog, index) => (
//               <div
//                 key={blog._id}
//                 className={`flex-shrink-0 cursor-pointer px-2`}
//                 style={{ width: `${cardWidth}%` }}
//                 onClick={() => handleCardClick(blog)}
//               >
//                 <BlogCard
//                   onMouseEnter={handleMouseEnter}
//                   onMouseLeave={handleMouseLeave}
//                   activity={blog}
//                 />
//               </div>
//             ))}
//           </div>
//         </div>

//         {/* Slider Controls */}
//         {hasMultipleSlides && (
//           <div className="mt-6 relative px-4 md:px-8 lg:px-12 flex flex-col sm:flex-row items-center justify-between gap-4">
//             <div className="h-full w-20"></div>
//             {/* Dots Navigation */}
//             <div className="flex space-x-2 ">
//               {Array.from({ length: maxSlides + 1 }, (_, index) => {
//                 if (index === currentIndex) {
//                   return (
//                     <div
//                       key={index}
//                       className="w-12 sm:w-20 h-1.5 flex bg-gray-300 rounded-full overflow-hidden"
//                     >
//                       <div
//                         className="h-full bg-red-600 transition-all duration-100 ease-linear rounded-full"
//                         style={{ width: `${progress}%` }}
//                       />
//                     </div>
//                   );
//                 } else {
//                   return (
//                     <button
//                       key={index}
//                       onClick={() => handleGoToSlide(index)}
//                       onMouseEnter={handleMouseEnter}
//                       onMouseLeave={handleMouseLeave}
//                       className="w-3 h-1.5 rounded-full transition-all duration-300 bg-gray-300 hover:bg-gray-400"
//                       aria-label={`Go to slide ${index + 1}`}
//                     />
//                   );
//                 }
//               })}
//             </div>
//             {/* Navigation Arrows */}
//             {hasMultipleSlides && (
//               <div className="relative md:right-2 flex gap-x-2">
//                 <button
//                   onClick={handlePrevSlide}
//                   onMouseEnter={handleMouseEnter}
//                   onMouseLeave={handleMouseLeave}
//                   className="bg-primaryBtnHoverColor hover:scale-[101%] active:scale-95 text-white p-2 rounded-full transition-all duration-300 z-10"
//                   aria-label="Previous slide"
//                 >
//                   <ArrowLeft className="w-6 h-6" />
//                 </button>

//                 <button
//                   onClick={handleNextSlide}
//                   onMouseEnter={handleMouseEnter}
//                   onMouseLeave={handleMouseLeave}
//                   className=" bg-primaryBtnHoverColor hover:scale-[101%] active:scale-95 text-white p-2 rounded-full transition-all duration-300 z-10"
//                   aria-label="Next slide"
//                 >
//                   <ArrowRight className="w-6 h-6" />
//                 </button>
//               </div>
//             )}
//           </div>
//         )}
//       </div>
//     </div>
//   );
// };

// export default AdditionalInformation;
